export const EMBED_PROTOCOL_VERSION = 2 as const;
export const TICKET_EXCHANGE_PROTOCOL_VERSION = 1 as const;

export const EMBEDDED_ROUTES = [
  "/dashboard",
  "/loans",
  "/lenders",
  "/posting",
  "/reconciliation",
  "/reports",
  "/settings",
] as const;

export type EmbeddedRoute = (typeof EMBEDDED_ROUTES)[number];

export const LOAN_ACCOUNT_PURPOSES = [
  "bank_account",
  "principal_liability",
  "interest_expense",
  "accrued_interest_payable",
  "financing_fee_expense",
  "loan_clearing",
  "deferred_financing_costs",
] as const;

export type LoanAccountPurpose = (typeof LOAN_ACCOUNT_PURPOSES)[number];

export const CONFIGURABLE_LOAN_EVENT_TYPES = [
  "funding",
  "interest_accrual",
  "payment_application",
  "cash_settlement",
  "financing_fee_amortization",
] as const;

export type ConfigurableLoanEventType = (typeof CONFIGURABLE_LOAN_EVENT_TYPES)[number];
export type LoanEventTypeOptionId = "funding" | "interest_accrual" | "payment_processing" | "financing_fee_amortization";

export interface LoanEventTypeOption {
  accountPurposes: readonly LoanAccountPurpose[];
  description: string;
  eventTypes: readonly ConfigurableLoanEventType[];
  id: LoanEventTypeOptionId;
  label: string;
}

export const LOAN_EVENT_TYPE_OPTIONS: readonly LoanEventTypeOption[] = [
  { accountPurposes: ["bank_account", "principal_liability"], description: "Record the initial loan proceeds and principal liability.", eventTypes: ["funding"], id: "funding", label: "Funding" },
  { accountPurposes: ["interest_expense", "accrued_interest_payable"], description: "Accrue interest expense and the related payable from the active schedule.", eventTypes: ["interest_accrual"], id: "interest_accrual", label: "Interest accrual" },
  { accountPurposes: ["principal_liability", "accrued_interest_payable", "financing_fee_expense", "loan_clearing", "bank_account"], description: "Record payment application and cash settlement as a paired accounting workflow.", eventTypes: ["payment_application", "cash_settlement"], id: "payment_processing", label: "Payment processing" },
  { accountPurposes: ["financing_fee_expense", "deferred_financing_costs"], description: "Amortize deferred financing costs over the loan schedule.", eventTypes: ["financing_fee_amortization"], id: "financing_fee_amortization", label: "Financing fee amortization" },
] as const;

export const DEFAULT_LOAN_EVENT_TYPES = CONFIGURABLE_LOAN_EVENT_TYPES;

export function normalizeLoanEventTypes(value: unknown): ConfigurableLoanEventType[] {
  if (!Array.isArray(value)) throw new Error("Loan event types must be an array");
  const normalized = [...new Set(value)];
  if (normalized.length === 0 || normalized.some((item) => typeof item !== "string" || !(CONFIGURABLE_LOAN_EVENT_TYPES as readonly string[]).includes(item))) {
    throw new Error("Select at least one supported loan event type");
  }
  const hasApplication = normalized.includes("payment_application");
  const hasSettlement = normalized.includes("cash_settlement");
  if (hasApplication !== hasSettlement) throw new Error("Payment application and cash settlement must be enabled together");
  return CONFIGURABLE_LOAN_EVENT_TYPES.filter((item) => normalized.includes(item));
}

export function enabledLoanEventOptionIds(eventTypes: readonly ConfigurableLoanEventType[]): LoanEventTypeOptionId[] {
  return LOAN_EVENT_TYPE_OPTIONS.filter((option) => option.eventTypes.every((eventType) => eventTypes.includes(eventType))).map((option) => option.id);
}

export function loanAccountPurposesForEventTypes(eventTypes: readonly ConfigurableLoanEventType[]): LoanAccountPurpose[] {
  const enabled = new Set(eventTypes);
  return LOAN_ACCOUNT_PURPOSES.filter((purpose) => LOAN_EVENT_TYPE_OPTIONS.some((option) => option.eventTypes.every((eventType) => enabled.has(eventType)) && option.accountPurposes.includes(purpose)));
}

export interface AppReadyMessage {
  type: "LM_APP_READY";
  protocolVersion: typeof EMBED_PROTOCOL_VERSION;
}

export interface BootstrapTicketMessage {
  type: "LM_BOOTSTRAP_TICKET";
  protocolVersion: typeof EMBED_PROTOCOL_VERSION;
  requestId: string;
  ticket: string;
}

export interface SessionEstablishedMessage {
  type: "LM_SESSION_ESTABLISHED";
  protocolVersion: typeof EMBED_PROTOCOL_VERSION;
  requestId: string;
}

export interface ResizeMessage {
  type: "LM_RESIZE";
  protocolVersion: typeof EMBED_PROTOCOL_VERSION;
  height: number;
}

export interface ScrollToTopMessage {
  type: "LM_SCROLL_TO_TOP";
  protocolVersion: typeof EMBED_PROTOCOL_VERSION;
}

export interface SessionExpiredMessage {
  type: "LM_SESSION_EXPIRED";
  protocolVersion: typeof EMBED_PROTOCOL_VERSION;
  correlationId: string;
}

export type AuthenticationFailureCode = "TICKET_EXCHANGE_FAILED" | "TICKET_INVALID";

export interface AuthenticationFailedMessage {
  type: "LM_AUTHENTICATION_FAILED";
  protocolVersion: typeof EMBED_PROTOCOL_VERSION;
  code: AuthenticationFailureCode;
  correlationId: string;
  requestId: string;
  stage: "ticket-exchange";
}

export type AppToLoaderMessage =
  | AppReadyMessage
  | AuthenticationFailedMessage
  | ResizeMessage
  | ScrollToTopMessage
  | SessionEstablishedMessage
  | SessionExpiredMessage;

export function isBootstrapTicketMessage(value: unknown): value is BootstrapTicketMessage {
  return (
    isRecord(value) &&
    value.type === "LM_BOOTSTRAP_TICKET" &&
    value.protocolVersion === EMBED_PROTOCOL_VERSION &&
    isBoundedIdentifier(value.requestId) &&
    typeof value.ticket === "string" &&
    value.ticket.length >= 32 &&
    value.ticket.length <= 200
  );
}

export function isEmbeddedRoute(value: string): value is EmbeddedRoute {
  return (EMBEDDED_ROUTES as readonly string[]).includes(value);
}

export function isAppToLoaderMessage(value: unknown): value is AppToLoaderMessage {
  if (!isRecord(value) || value.protocolVersion !== EMBED_PROTOCOL_VERSION) {
    return false;
  }

  switch (value.type) {
    case "LM_APP_READY":
      return true;
    case "LM_AUTHENTICATION_FAILED":
      return (
        isAuthenticationFailureCode(value.code) &&
        isBoundedIdentifier(value.correlationId) &&
        isBoundedIdentifier(value.requestId) &&
        value.stage === "ticket-exchange"
      );
    case "LM_RESIZE":
      return typeof value.height === "number" && Number.isFinite(value.height);
    case "LM_SCROLL_TO_TOP":
      return true;
    case "LM_SESSION_ESTABLISHED":
      return typeof value.requestId === "string";
    case "LM_SESSION_EXPIRED":
      return typeof value.correlationId === "string";
    default:
      return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isAuthenticationFailureCode(value: unknown): value is AuthenticationFailureCode {
  return value === "TICKET_EXCHANGE_FAILED" || value === "TICKET_INVALID";
}

function isBoundedIdentifier(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 100;
}
